import { Link } from "react-router-dom";
import { ArrowRight, Download, Settings, MousePointerClick, CheckCircle2, HelpCircle, ShieldCheck } from "lucide-react";

function GuideLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="border-b border-[#1E2A3F] sticky top-0 z-40 bg-[#0B0F17]/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center transition-opacity hover:opacity-80">
            <img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/how-to-download" className="text-slate-300 hover:text-white transition-colors hidden sm:inline">Download guide</Link>
            <Link to="/faq" className="text-slate-300 hover:text-white transition-colors hidden sm:inline">FAQ</Link>
            <Link to="/" className="text-slate-300 hover:text-white transition-colors">Back to home</Link>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">{title}</h1>
        {subtitle && <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-10">{subtitle}</p>}
        {children}
      </main>
      <footer className="border-t border-[#1E2A3F] py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-3 text-xs text-slate-500">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>2026 © LinkViewer.PRO. All rights reserved.</div>
            <div className="flex gap-4">
              <Link to="/how-to-download" className="hover:text-slate-300">Download guide</Link>
              <Link to="/faq" className="hover:text-slate-300">FAQ</Link>
              <Link to="/terms" className="hover:text-slate-300">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
              <Link to="/refund" className="hover:text-slate-300">Refund</Link>
            </div>
          </div>
          <div className="text-slate-600 text-center sm:text-left">LinkedIn is a trademark of LinkedIn Corporation. LinkViewer.PRO is an independent tool and is not affiliated with, endorsed by or sponsored by LinkedIn.</div>
        </div>
      </footer>
    </div>
  );
}

const STEPS = [
  {
    icon: Settings,
    title: "Open your data settings",
    body: 'On LinkedIn (desktop), click your photo (Me), then Settings & Privacy, then Data privacy, then Get a copy of your data.',
  },
  {
    icon: MousePointerClick,
    title: "Choose Connections",
    body: 'Select "Want something in particular?", tick Connections, and click Request archive. LinkedIn may ask for your password.',
  },
  {
    icon: Download,
    title: "Download and upload",
    body: "LinkedIn emails you a download link, usually within minutes and sometimes up to 24 hours. Download the ZIP and upload it to LinkViewer.PRO as it is — there's no need to unzip it.",
  },
];

export function HowToDownload() {
  return (
    <GuideLayout
      title="How to download your LinkedIn connections"
      subtitle="It takes about 2 minutes, and LinkedIn emails you the file."
    >
      <div className="space-y-4" data-testid="how-to-steps">
        {STEPS.map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 flex gap-5 hover:border-blue-500/30 transition-colors">
            <div className="shrink-0">
              <div className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center">
                <s.icon className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono-niq text-xs text-slate-500">STEP {String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-display text-lg font-semibold mb-1.5">{s.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-6 flex items-start gap-4">
        <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-slate-400 text-sm leading-relaxed">
          LinkViewer.PRO works only with the file you upload. It does no web browsing, scraping or outside lookups — it simply reads your export and categorizes it so you can search and filter it.
        </p>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Link to="/register" data-testid="guide-cta-register" className="bg-blue-600 hover:bg-blue-500 px-6 py-3.5 rounded-lg font-medium transition-colors flex items-center gap-2">
          Analyze my network free <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/faq" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5">
          Read the FAQ <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </GuideLayout>
  );
}

const FAQS = [
  {
    q: "Why do most of my contacts have no email address?",
    a: "LinkedIn only includes a connection's email in your export if that person has allowed connections to download it. Most people never turn this on, so the email column is usually mostly empty. This comes from LinkedIn, not from LinkViewer.PRO, and no tool working from your export can recover those emails.",
  },
  {
    q: "Do you scrape LinkedIn or look people up online?",
    a: "No. LinkViewer.PRO works only with the file you upload. It does no web browsing, scraping or outside lookups.",
  },
  {
    q: "What happens to my data?",
    a: "Your file is processed only for your account and is never shared with other users or third parties. You can delete a dataset at any time from the Datasets section of the Upload page, and deleted data is removed immediately and permanently.",
  },
  {
    q: "Which files can I upload?",
    a: "LinkedIn's export as a ZIP or CSV, or a CSV or Excel file with the same columns. Up to 25,000 contacts per file.",
  },
  {
    q: 'What do "High", "Medium" and "Low" confidence mean?',
    a: "High: the job title and company clearly show the sector, seniority or region. Medium: inferred from partial information, such as a general title or a company with several activities. Low: a best guess from very little information — check before relying on it.",
  },
  {
    q: "What's in the free plan?",
    a: "One upload, full categorisation and 3 searches, viewable on screen. Exports to PDF and Excel and unlimited searches need Pro.",
  },
  {
    q: "How much is Pro, and can I cancel?",
    a: "$12 a month, or $99 a year. You can cancel any time, and your plan stays active until the end of the paid period. Payments are handled securely by Paddle.",
  },
  {
    q: "Can I upload an updated export later?",
    a: "Yes. Upload a new export at any time to refresh your network.",
  },
];

export function Faq() {
  return (
    <GuideLayout
      title="Frequently asked questions"
      subtitle="Everything you need to know about uploading, categorisation, privacy and pricing."
    >
      <div className="space-y-4" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <div key={i} data-testid={`faq-item-${i}`} className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 hover:border-blue-500/30 transition-colors">
            <div className="flex items-start gap-3 mb-2">
              <HelpCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <h3 className="font-display text-base sm:text-lg font-semibold">{f.q}</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed pl-8">{f.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-[#27334D] bg-[#111726] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium mb-0.5">Ready to see your network clearly?</h4>
            <p className="text-slate-500 text-sm">Not sure how to get your file? Follow the 2-minute download guide.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/how-to-download" className="text-sm text-slate-300 hover:text-white border border-[#27334D] hover:border-blue-500/40 px-4 py-2.5 rounded-lg transition-colors">Download guide</Link>
          <Link to="/register" data-testid="faq-cta-register" className="bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm">
            Start free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </GuideLayout>
  );
}
